import useDisableScroll from '@/hooks/useDisableScroll';

import { FORM_DATA_KEY_FACTORY, useFormDataQuery } from '@/api/getters';
import { updateEmail } from '@/api/setters';
import { FormData } from '@/api/types';
import Pencil from '@/assets/pencil.svg?react';
import ChatMessage from '@/components/ChatMessage';
import Button, { ButtonLoading } from '@/components/ui/Button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/Form';
import { Input } from '@/components/ui/Input';
import { KASKA_CONSULTANT } from '@/consts';
import { validateNewEmail } from '@/features/v2/summaryForms/Validate';

import { useLingui } from '@lingui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

interface EmailFormValues {
  email: string;
}

// On success we optimistically patch the cached form data so the gate
// closes immediately, then invalidate to reconcile with the server.
const useSaveLeadEmail = () => {
  const { id: hash } = useParams();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (email: string) => {
      const res = await updateEmail(hash ?? '', email);
      if (!res.ok) {
        throw new Error('Failed to save email');
      }
      return email;
    },
    onSuccess: (email) => {
      queryClient.setQueryData<FormData>(FORM_DATA_KEY_FACTORY(hash), (old) =>
        old
          ? { ...old, is_email_empty: false, user: { ...old.user, email } }
          : old
      );
      queryClient.invalidateQueries({ queryKey: FORM_DATA_KEY_FACTORY(hash) });
    },
  });

  return {
    saveLeadEmail: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
};

const EmailGateDialog = () => {
  const { i18n } = useLingui();
  const { data } = useFormDataQuery();
  const { saveLeadEmail, isPending } = useSaveLeadEmail();
  const methods = useForm<EmailFormValues>({
    mode: 'onBlur',
    defaultValues: { email: '' },
  });

  const isOpen = !!data && data.is_email_empty;

  useDisableScroll(isOpen);

  if (!isOpen) return null;

  const submit = async ({ email }: EmailFormValues) => {
    try {
      await saveLeadEmail(email);
    } catch {
      methods.setError('email', {
        message: i18n.t('Nie udało się zapisać, spróbuj ponownie.'),
      });
    }
  };

  return createPortal(
    <div
      data-allow-scroll
      className="fixed left-0 top-0 z-[9999] flex h-[100dvh] w-full items-center justify-center overflow-y-auto bg-black/20 p-4 backdrop-blur-sm"
    >
      <div className="flex max-h-[85dvh] w-full max-w-[343px] flex-col overflow-y-auto rounded-[20px] bg-white shadow-xl md:max-w-[500px] md:rounded-[28px]">
        <div className="flex items-center justify-center gap-x-2 border-b border-dark-gray-100 px-6 py-5 text-blue-600 lg:px-8">
          <Pencil className="size-5 shrink-0 md:size-6" />
          <h3 className="text-center">{i18n.t('Uzupełnij adres e-mail')}</h3>
        </div>
        <Form {...methods}>
          <form onSubmit={methods.handleSubmit(submit)}>
            <div className="flex flex-col gap-y-5 px-6 py-5">
              <ChatMessage
                variant="primary"
                className="items-start px-0 pb-0 md:px-0 lg:flex-col lg:px-0"
                consultant={KASKA_CONSULTANT}
                hideCloseButton
                description={`${i18n.t('Cześć,')} ${data.user.first_name}! ${i18n.t(
                  'Widzę, że podczas rozmowy nie został podany Twój adres e-mail. Uzupełnij go poniżej, abyśmy mogli bezpiecznie prowadzić Twoją sprawę i przesyłać Ci wszystkie ważne informacje.'
                )}`}
              />
              <FormField
                name="email"
                control={methods.control}
                rules={{ validate: validateNewEmail }}
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>{i18n.t('Podaj swój adres e-mail')}</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        label={i18n.t('Adres e-mail')}
                        error={fieldState.error?.message}
                        success={
                          fieldState.isTouched &&
                          !fieldState.error &&
                          !!field.value
                        }
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          if (fieldState.error) methods.trigger('email');
                        }}
                        onFocus={(e) =>
                          e.target.scrollIntoView?.({
                            block: 'center',
                            behavior: 'smooth',
                          })
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            <div className="flex items-center justify-center border-t border-t-dark-gray-100 bg-gray-200 px-6 py-5 lg:px-8">
              <Button
                type="submit"
                loading={isPending}
                disabled={isPending}
                className="w-full md:w-auto"
              >
                {i18n.t('Zapisz i przejdź dalej')}
                <ButtonLoading />
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>,
    document.body
  );
};

export default EmailGateDialog;
